import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { defaultStaleTime, defaultGcTime } from '@/shared/constants';

export interface GameCamera {
  // Identity
  id: number;              // gameCameraId (actual gameCamera record ID)
  uniqueId: string;        // gameCamera uniqueId
  cameraId: number | null; // LEGACY: venue setup camera this was copied from
  gameId: number;
  camIndex: number;        // Camera index (0, 1, 2, etc.)
  version: number | null;   // Version for optimistic locking
  
  // Camera properties (directly from gameCamera record)
  displayName: string | null;
  positionX: number;
  positionY: number;
  positionZ: number;
  forwardX: number;
  forwardY: number;
  forwardZ: number;
  tilt: number | null;
  roll: number | null;
  distMax: number | null;
  distMin: number | null;
  fovMax: number | null;
  fovMin: number | null;
  offset: number;
  isLive: boolean | null;
  isHidden?: boolean;      // Per-org hide flag (server-resolved from orgHiddenCameraName rules)
  statusMessage?: string | null;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  
  // Base camera info for reference
  baseCameraInfo: {
    camIndex: number;
    hardwareId: string | null;
  };
  
  // Setup context (minimal, for reference only)
  cameraSetup: {
    id: number;
    name: string;
    units: string;
  } | null;
  
  // Lens info (for video processing)
  lens: {
    id: number;
    name: string;
    curveRadius: number[];
    curveAngle: number[];
  } | null;
  
  // Tracking configuration
  trackingConfig: {
    id: number;
    name: string;
    slerpRate: number;
    lerpRate: number;
    vDamp: number;
  } | null;
}

export const getGameCamerasQueryKey = (gameId: string | number) => ['GameCameras', gameId];

export const useGameCamerasQuery = (gameId?: string | number) => {
  return useQuery({
    queryKey: getGameCamerasQueryKey(gameId!),
    queryFn: async (): Promise<GameCamera[]> => {
      const { data } = await get<GameCamera[]>(`/v1.0/games/${gameId}/cameras`);
      return data || [];
    },
    enabled: !!gameId,
    staleTime: defaultStaleTime,
    gcTime: defaultGcTime,
  });
};

export default useGameCamerasQuery;